import * as THREE from 'three'
import type {
  AnimationClipData,
  AnimationPlayerHandle,
  AnimationService,
  AnimationTrackData,
  CameraAnimationTargetRef,
} from '@/shared/types/animation'
import type { Disposable } from '@/shared/types/core'
import {
  createSemanticCameraMotion,
  isSemanticCameraMotionLeafTemplate,
  parseSemanticCameraMotionTemplate,
  type SemanticCameraMotionContext,
  type SemanticCameraMotionKeyframe,
  type SemanticCameraMotionOptions,
  type SemanticCameraMotionSequenceTemplate,
  type SemanticCameraMotionSphericalOffset,
  type SemanticCameraMotionStartOffset,
  type SemanticCameraMotionTemplate,
  type SemanticCameraMotionVector3Like,
} from './semanticCameraMotion'

export type SemanticCameraMotionPlanSegmentKind = 'motion' | 'hold'

export interface SemanticCameraMotionCameraState {
  position: [number, number, number]
  target: [number, number, number]
  up: [number, number, number]
  fov: number
  roll: number
}

export interface SemanticCameraMotionPlanSegment {
  kind: SemanticCameraMotionPlanSegmentKind
  templateId: string
  clip: AnimationClipData
  keyframes: SemanticCameraMotionKeyframe[]
  startTime: number
  endTime: number
}

export interface SemanticCameraMotionPlanDiagnostics {
  distance: number
  clamped: boolean
  leafCount: number
  segmentCount: number
}

export interface SemanticCameraMotionPlan {
  segments: SemanticCameraMotionPlanSegment[]
  motionDuration: number
  duration: number
  initialCamera: SemanticCameraMotionCameraState
  finalCamera: SemanticCameraMotionCameraState
  diagnostics: SemanticCameraMotionPlanDiagnostics
}

interface SemanticCameraMotionPlanBuilder {
  segments: SemanticCameraMotionPlanSegment[]
  elapsed: number
  clamped: boolean
  leafCount: number
}

interface ResolvePlanContext {
  rootCamera: SemanticCameraMotionCameraState
  builder: SemanticCameraMotionPlanBuilder
}

const EPSILON = 1e-8
const WORLD_UP = new THREE.Vector3(0, 0, 1)
const FALLBACK_UP_AXES = [new THREE.Vector3(0, 1, 0), new THREE.Vector3(1, 0, 0)]
let nextPlanPlayerId = 1

export function createSemanticCameraMotionPlan(
  template: unknown,
  context: SemanticCameraMotionContext,
  options: SemanticCameraMotionOptions
): SemanticCameraMotionPlan {
  const normalizedTemplate = parseSemanticCameraMotionTemplate(template)
  const motionDuration = parsePositiveFiniteNumber(options?.duration, 'duration')
  const initialCamera = normalizeCameraState(context)
  const builder: SemanticCameraMotionPlanBuilder = {
    segments: [],
    elapsed: 0,
    clamped: false,
    leafCount: 0,
  }
  const finalCamera = resolveTemplatePlan(
    normalizedTemplate,
    cloneCameraState(initialCamera),
    motionDuration,
    { rootCamera: initialCamera, builder }
  )

  return {
    segments: builder.segments,
    motionDuration,
    duration: builder.elapsed,
    initialCamera,
    finalCamera,
    diagnostics: {
      distance: tupleDistance(initialCamera.position, initialCamera.target),
      clamped: builder.clamped,
      leafCount: builder.leafCount,
      segmentCount: builder.segments.length,
    },
  }
}

function resolveTemplatePlan(
  template: SemanticCameraMotionTemplate,
  inputCamera: SemanticCameraMotionCameraState,
  motionDuration: number,
  context: ResolvePlanContext
): SemanticCameraMotionCameraState {
  if (isSemanticCameraMotionLeafTemplate(template)) {
    const leafInput: SemanticCameraMotionCameraState = {
      ...cloneCameraState(inputCamera),
      fov: context.rootCamera.fov,
      roll: context.rootCamera.roll,
    }
    const result = createSemanticCameraMotion(
      template,
      { camera: leafInput },
      { duration: motionDuration }
    )
    const lastFrame = result.keyframes[result.keyframes.length - 1]
    if (!lastFrame) {
      throw new Error(`Semantic camera motion leaf "${template.id}" produced no keyframes`)
    }

    appendPlanSegment(context.builder, {
      kind: 'motion',
      templateId: template.id,
      clip: result.clip,
      keyframes: result.keyframes,
    })
    context.builder.clamped = context.builder.clamped || result.diagnostics.clamped
    context.builder.leafCount += 1

    return cameraStateFromKeyframe(lastFrame, context.rootCamera.roll)
  }

  return resolveSequencePlan(template, inputCamera, motionDuration, context)
}

function resolveSequencePlan(
  template: SemanticCameraMotionSequenceTemplate,
  inputCamera: SemanticCameraMotionCameraState,
  motionDuration: number,
  context: ResolvePlanContext
): SemanticCameraMotionCameraState {
  if (template.params.steps.length === 0) {
    throw new Error(
      `Semantic camera motion sequence "${template.id}" must contain at least one step to create a plan`
    )
  }
  const totalWeight = template.params.steps.reduce(
    (sum, step) => sum + (step.durationWeight ?? 1),
    0
  )
  let currentCamera = cloneCameraState(inputCamera)

  for (let index = 0; index < template.params.steps.length; index++) {
    const step = template.params.steps[index]
    if (index > 0) {
      currentCamera = relocateCameraState(
        currentCamera,
        step.startOffset,
        context.rootCamera.fov,
        context.rootCamera.roll
      )
    }
    const stepDuration = motionDuration * ((step.durationWeight ?? 1) / totalWeight)
    currentCamera = resolveTemplatePlan(step.template, currentCamera, stepDuration, context)
  }

  const holdDuration = template.holdDuration ?? 0
  if (holdDuration > 0) {
    const holdKeyframes = createHoldKeyframes(currentCamera, holdDuration)
    appendPlanSegment(context.builder, {
      kind: 'hold',
      templateId: template.id,
      clip: createHoldClip(template, holdKeyframes, holdDuration),
      keyframes: holdKeyframes,
    })
  }

  return currentCamera
}

function appendPlanSegment(
  builder: SemanticCameraMotionPlanBuilder,
  segment: Omit<SemanticCameraMotionPlanSegment, 'startTime' | 'endTime'>
): void {
  const duration = segment.clip.duration ?? 0
  if (!Number.isFinite(duration) || duration <= 0) {
    throw new Error(
      `Semantic camera motion plan segment "${segment.templateId}" has invalid duration`
    )
  }
  const startTime = builder.elapsed
  const endTime = startTime + duration
  builder.segments.push({ ...segment, startTime, endTime })
  builder.elapsed = endTime
}

function relocateCameraState(
  input: SemanticCameraMotionCameraState,
  offset: SemanticCameraMotionStartOffset | undefined,
  rootFov: number,
  rootRoll: number
): SemanticCameraMotionCameraState {
  const previousPosition = tupleToVector(input.position)
  const previousTarget = tupleToVector(input.target)
  const position = applySphericalOffset(previousPosition, previousTarget, offset?.camera)
  const target = applySphericalOffset(previousTarget, position, offset?.target)
  if (position.distanceToSquared(target) <= EPSILON) {
    throw new Error('Semantic camera motion start offset must not make camera equal target')
  }

  return {
    position: vectorToTuple(position),
    target: vectorToTuple(target),
    up: vectorToTuple(deriveCameraUp(position, target, rootRoll)),
    fov: rootFov,
    roll: rootRoll,
  }
}

function applySphericalOffset(
  point: THREE.Vector3,
  center: THREE.Vector3,
  offset: SemanticCameraMotionSphericalOffset | undefined
): THREE.Vector3 {
  if (!offset) {
    return point.clone()
  }

  const vector = point.clone().sub(center)
  if (vector.lengthSq() <= EPSILON) {
    throw new Error('Semantic camera motion spherical offset requires distinct point and center')
  }
  const yaw = THREE.MathUtils.degToRad(offset.yaw ?? 0)
  const pitch = THREE.MathUtils.degToRad(offset.pitch ?? 0)
  const distance = vector.length() * (offset.distanceScale ?? 1)
  const yawed = vector.applyAxisAngle(WORLD_UP, yaw)
  const pitchAxis = yawed.clone().cross(WORLD_UP)
  if (pitchAxis.lengthSq() <= EPSILON) {
    pitchAxis.copy(createPerpendicularAxis(yawed))
  } else {
    pitchAxis.normalize()
  }

  return center.clone().add(yawed.applyAxisAngle(pitchAxis, pitch).setLength(distance))
}

function deriveCameraUp(
  position: THREE.Vector3,
  target: THREE.Vector3,
  roll: number
): THREE.Vector3 {
  const forward = target.clone().sub(position).normalize()
  let up = WORLD_UP.clone().addScaledVector(forward, -WORLD_UP.dot(forward))
  if (up.lengthSq() <= EPSILON) {
    for (const candidate of FALLBACK_UP_AXES) {
      up = candidate.clone().addScaledVector(forward, -candidate.dot(forward))
      if (up.lengthSq() > EPSILON) {
        break
      }
    }
  }
  up.normalize()
  if (roll !== 0) {
    up.applyAxisAngle(forward, roll)
  }
  return up.normalize()
}

function createPerpendicularAxis(vector: THREE.Vector3): THREE.Vector3 {
  const normalized = vector.clone().normalize()
  for (const candidate of FALLBACK_UP_AXES) {
    const axis = normalized.clone().cross(candidate)
    if (axis.lengthSq() > EPSILON) {
      return axis.normalize()
    }
  }
  return new THREE.Vector3(1, 0, 0)
}

function createHoldKeyframes(
  camera: SemanticCameraMotionCameraState,
  duration: number
): SemanticCameraMotionKeyframe[] {
  const frame = {
    position: [...camera.position] as [number, number, number],
    target: [...camera.target] as [number, number, number],
    fov: camera.fov,
    roll: camera.roll,
  }
  return [
    { time: 0, ...frame },
    {
      time: duration,
      position: [...frame.position],
      target: [...frame.target],
      fov: frame.fov,
      roll: frame.roll,
    },
  ]
}

function createHoldClip(
  template: SemanticCameraMotionSequenceTemplate,
  keyframes: SemanticCameraMotionKeyframe[],
  duration: number
): AnimationClipData {
  const target: CameraAnimationTargetRef = { kind: 'camera', active: 'bind-on-play' }
  const times = keyframes.map((frame) => frame.time)
  const timing: Pick<AnimationTrackData, 'interpolation'> = { interpolation: 'step' }

  return {
    name: `${template.name} 定格`,
    duration,
    loopMode: 'once',
    metadata: {
      semanticCameraMotion: true,
      semanticCameraMotionPlanHold: true,
      semantic: template.semantic,
      templateId: template.id,
    },
    tracks: [
      createHoldTrack(
        target,
        'position',
        'vec3',
        times,
        keyframes.flatMap((frame) => frame.position),
        timing
      ),
      createHoldTrack(
        target,
        'target',
        'vec3',
        times,
        keyframes.flatMap((frame) => frame.target),
        timing
      ),
      createHoldTrack(
        target,
        'fov',
        'number',
        times,
        keyframes.map((frame) => frame.fov),
        timing
      ),
      createHoldTrack(
        target,
        'roll',
        'number',
        times,
        keyframes.map((frame) => frame.roll),
        timing
      ),
    ],
  }
}

function createHoldTrack(
  target: CameraAnimationTargetRef,
  propertyPath: string,
  valueType: AnimationTrackData['valueType'],
  times: number[],
  values: number[],
  timing: Pick<AnimationTrackData, 'interpolation'>
): AnimationTrackData {
  return { target, propertyPath, valueType, times, values, ...timing }
}

function cameraStateFromKeyframe(
  frame: SemanticCameraMotionKeyframe,
  rootRoll: number
): SemanticCameraMotionCameraState {
  const position = tupleToVector(frame.position)
  const target = tupleToVector(frame.target)
  return {
    position: [...frame.position],
    target: [...frame.target],
    up: vectorToTuple(deriveCameraUp(position, target, rootRoll)),
    fov: frame.fov,
    roll: frame.roll,
  }
}

function normalizeCameraState(
  context: SemanticCameraMotionContext
): SemanticCameraMotionCameraState {
  if (!context || typeof context !== 'object' || !context.camera) {
    throw new Error('Semantic camera motion context.camera must be an object')
  }
  const position = normalizeVector(context.camera.position, 'camera.position')
  const target = normalizeVector(context.camera.target, 'camera.target')
  const up = normalizeVector(context.camera.up, 'camera.up')
  const fov = parsePositiveFiniteNumber(context.camera.fov, 'camera.fov')
  const roll =
    context.camera.roll === undefined ? 0 : parseFiniteNumber(context.camera.roll, 'camera.roll')

  if (tupleDistance(position, target) <= EPSILON) {
    throw new Error('Semantic camera motion camera.position must not equal camera.target')
  }
  const upVector = tupleToVector(up)
  if (upVector.lengthSq() <= EPSILON) {
    throw new Error('Semantic camera motion camera.up must not be a zero vector')
  }

  return { position, target, up: vectorToTuple(upVector.normalize()), fov, roll }
}

function normalizeVector(
  value: SemanticCameraMotionVector3Like,
  fieldName: string
): [number, number, number] {
  if (Array.isArray(value)) {
    if (value.length !== 3) {
      throw new Error(`Semantic camera motion ${fieldName} must contain exactly 3 coordinates`)
    }
    return [
      parseFiniteNumber(value[0], `${fieldName}.x`),
      parseFiniteNumber(value[1], `${fieldName}.y`),
      parseFiniteNumber(value[2], `${fieldName}.z`),
    ]
  }
  if (!value || typeof value !== 'object') {
    throw new Error(`Semantic camera motion ${fieldName} must be a vector tuple or object`)
  }
  const objectValue = value as { x: unknown; y: unknown; z: unknown }
  return [
    parseFiniteNumber(objectValue.x, `${fieldName}.x`),
    parseFiniteNumber(objectValue.y, `${fieldName}.y`),
    parseFiniteNumber(objectValue.z, `${fieldName}.z`),
  ]
}

function parseFiniteNumber(value: unknown, fieldName: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`Semantic camera motion ${fieldName} must be a finite number`)
  }
  return value
}

function parsePositiveFiniteNumber(value: unknown, fieldName: string): number {
  const numberValue = parseFiniteNumber(value, fieldName)
  if (numberValue <= 0) {
    throw new Error(`Semantic camera motion ${fieldName} must be a positive finite number`)
  }
  return numberValue
}

function tupleToVector(value: readonly [number, number, number]): THREE.Vector3 {
  return new THREE.Vector3(value[0], value[1], value[2])
}

function vectorToTuple(value: THREE.Vector3): [number, number, number] {
  return [value.x, value.y, value.z]
}

function tupleDistance(
  first: readonly [number, number, number],
  second: readonly [number, number, number]
): number {
  return tupleToVector(first).distanceTo(tupleToVector(second))
}

function cloneCameraState(
  camera: SemanticCameraMotionCameraState
): SemanticCameraMotionCameraState {
  return {
    position: [...camera.position],
    target: [...camera.target],
    up: [...camera.up],
    fov: camera.fov,
    roll: camera.roll,
  }
}

export type SemanticCameraMotionPlanPlayerState =
  | 'idle'
  | 'playing'
  | 'paused'
  | 'stopped'
  | 'completed'

export type SemanticCameraMotionPlanPlayerEvent =
  | 'play'
  | 'pause'
  | 'resume'
  | 'stop'
  | 'complete'
  | 'segmentChange'

export interface SemanticCameraMotionPlanPlayerEventPayload {
  player: SemanticCameraMotionPlanPlayer
  time: number
  segmentIndex: number
}

export interface SemanticCameraMotionPlanPlayOptions {
  id?: string
  autoplay?: boolean
}

export class SemanticCameraMotionPlanPlayer implements Disposable {
  readonly id: string
  readonly plan: SemanticCameraMotionPlan

  private readonly listeners = new Map<
    SemanticCameraMotionPlanPlayerEvent,
    Set<(payload: SemanticCameraMotionPlanPlayerEventPayload) => void>
  >()
  private _state: SemanticCameraMotionPlanPlayerState = 'idle'
  private _segmentIndex = -1
  private currentPlayer: AnimationPlayerHandle | null = null
  private completeSubscription: Disposable | null = null
  private stopSubscription: Disposable | null = null
  private stoppingCurrentPlayer = false

  constructor(
    private readonly animationService: AnimationService,
    plan: SemanticCameraMotionPlan,
    options: SemanticCameraMotionPlanPlayOptions = {}
  ) {
    if (plan.segments.length === 0) {
      throw new Error('Semantic camera motion plan must contain at least one segment')
    }
    this.id = options.id ?? `semantic-camera-motion-plan-${nextPlanPlayerId++}`
    this.plan = plan
    if (options.autoplay !== false) {
      this.play()
    }
  }

  get state(): SemanticCameraMotionPlanPlayerState {
    return this._state
  }

  get segmentIndex(): number {
    return this._segmentIndex
  }

  get time(): number {
    const segment = this.plan.segments[this._segmentIndex]
    if (!segment) {
      return this._state === 'completed' ? this.plan.duration : 0
    }
    return Math.min(this.plan.duration, segment.startTime + (this.currentPlayer?.time ?? 0))
  }

  play(): void {
    if (this._state === 'playing') {
      return
    }
    if (this._state === 'paused') {
      this.resume()
      return
    }
    this.clearCurrentPlayer(true)
    this._segmentIndex = 0
    this._state = 'playing'
    this.startCurrentSegment()
    this.emit('play')
  }

  pause(): void {
    if (this._state !== 'playing' || !this.currentPlayer) {
      return
    }
    this.currentPlayer.pause()
    this._state = 'paused'
    this.emit('pause')
  }

  resume(): void {
    if (this._state !== 'paused' || !this.currentPlayer) {
      return
    }
    this.currentPlayer.resume()
    this._state = 'playing'
    this.emit('resume')
  }

  stop(): void {
    if (this._state === 'stopped' || this._state === 'completed') {
      return
    }
    this.clearCurrentPlayer(true)
    this._state = 'stopped'
    this.emit('stop')
  }

  on(
    event: SemanticCameraMotionPlanPlayerEvent,
    callback: (payload: SemanticCameraMotionPlanPlayerEventPayload) => void
  ): Disposable {
    let callbacks = this.listeners.get(event)
    if (!callbacks) {
      callbacks = new Set()
      this.listeners.set(event, callbacks)
    }
    callbacks.add(callback)
    return { dispose: () => callbacks?.delete(callback) }
  }

  dispose(): void {
    this.stop()
    this.listeners.clear()
  }

  private startCurrentSegment(): void {
    const segment = this.plan.segments[this._segmentIndex]
    if (!segment) {
      this._state = 'completed'
      this.emit('complete')
      return
    }

    const currentPlayer = this.animationService.play(segment.clip, {
      id: `${this.id}-segment-${this._segmentIndex}`,
    })
    this.currentPlayer = currentPlayer
    this.completeSubscription = currentPlayer.on('complete', () => {
      this.detachCurrentSubscriptions()
      this.currentPlayer = null
      this._segmentIndex += 1
      if (this._segmentIndex >= this.plan.segments.length) {
        this._state = 'completed'
        this.emit('complete')
        return
      }
      this.startCurrentSegment()
      this.emit('segmentChange')
    })
    this.stopSubscription = currentPlayer.on('stop', () => {
      if (this.stoppingCurrentPlayer) {
        return
      }
      this.detachCurrentSubscriptions()
      this.currentPlayer = null
      this._state = 'stopped'
      this.emit('stop')
    })
  }

  private clearCurrentPlayer(stop: boolean): void {
    const currentPlayer = this.currentPlayer
    this.detachCurrentSubscriptions()
    this.currentPlayer = null
    if (stop && currentPlayer && currentPlayer.state !== 'completed') {
      this.stoppingCurrentPlayer = true
      currentPlayer.stop()
      this.stoppingCurrentPlayer = false
    }
  }

  private detachCurrentSubscriptions(): void {
    this.completeSubscription?.dispose()
    this.completeSubscription = null
    this.stopSubscription?.dispose()
    this.stopSubscription = null
  }

  private emit(event: SemanticCameraMotionPlanPlayerEvent): void {
    const callbacks = this.listeners.get(event)
    if (!callbacks) {
      return
    }
    const payload: SemanticCameraMotionPlanPlayerEventPayload = {
      player: this,
      time: this.time,
      segmentIndex: this._segmentIndex,
    }
    for (const callback of Array.from(callbacks)) {
      callback(payload)
    }
  }
}

export function playSemanticCameraMotionPlan(
  animationService: AnimationService,
  plan: SemanticCameraMotionPlan,
  options: SemanticCameraMotionPlanPlayOptions = {}
): SemanticCameraMotionPlanPlayer {
  return new SemanticCameraMotionPlanPlayer(animationService, plan, options)
}
